import { useEffect, useRef, useState } from "react";
import BaseCard from "../../../components/ui/BaseCard";
import { useQuery } from "@tanstack/react-query";
import BaseButton from "../../../components/ui/BaseButton";
import AddEditDepartmentModal from "./components/AddEditDepartmentModal";
import BaseDataTableV2 from "../../../components/ui/BaseDataTableV2";
import BaseDropdownV2 from "../../../components/ui/BaseDropdownV2";
import { deleteDepartment, fetchDepartments } from "../../../services/api/departments";
import { debounce } from "lodash";
import BaseTextField from "../../../components/ui/BaseTextField";
import BaseConfirmModal from "../../../components/ui/BaseConfirmModal";
import { useDispatch } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";
import UserProfileLink from "../../../components/ui/UserProfileLink";

const menuOptions = [
  {
    label: "Edit",
    value: "EDIT",
  },
  {
    label: "Delete",
    value: "DELETE",
  },
];

const CompanyDepartmentsTable = () => {
  const dispatch = useDispatch();
  const [isAddEditDepartmentOpen, setIsAddEditDepartmentOpen] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] = useState(false);

  const baseQueryParams = {
    limit: 20,
    page: 1,
    search: "",
  };
  const [queryParams, setQueryParams] = useState(baseQueryParams);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [departmentsList, setDepartmentsList] = useState([]);
  const [rowCount, setRowCount] = useState(0);

  const {
    data: departmentsData,
    isLoading: isDepartmentsLoading,
    refetch: refetchDepartments,
  } = useQuery({
    queryKey: ["departmentsData", queryParams.limit, queryParams.page, queryParams.search],
    queryFn: () => fetchDepartments(queryParams),
  });

  useEffect(() => {
    if (departmentsData?.data?.data) {
      setDepartmentsList(departmentsData.data.data);
      setRowCount(departmentsData.data.totalCount);
    }
  }, [departmentsData]);

  const columns = [
    {
      field: "name",
      headerName: "Department Name",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <p
          onClick={() => handleDepartmentView(params.row)}
          className="text-primary cursor-pointer"
        >
          {params.value}
        </p>
      ),
    },
    {
      field: "teams",
      headerName: "Teams",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <p>{params.value?.length ? params.value.map((t) => t.name).join(", ") : "No teams"}</p>
      ),
    },
    {
      field: "createdBy",
      headerName: "Created By",
      flex: 1,
      minWidth: 150,
      renderCell: (params) =>
        params.value ? (
          <UserProfileLink userId={params.value.id}>
            {`${params.value.firstName} ${params.value.lastName}`}
          </UserProfileLink>
        ) : (
          <p>N/A</p>
        ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      minWidth: 90,
      renderCell: (params) => (
        <BaseDropdownV2
          menuOptions={menuOptions}
          triggerElement={<i className="fa-solid fa-ellipsis cursor-pointer ml-4 text-base"></i>}
          onItemClick={(item) => handleItemClick(item, params.row)}
        />
      ),
    },
  ];

  const updateQueryParamsDebounced = useRef(
    debounce((query) => {
      setQueryParams((prev) => ({
        ...prev,
        search: query.trim() || "",
        page: 1,
      }));
    }, 500),
  ).current;

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    updateQueryParamsDebounced(query);
  };

  const handlePaginationChange = (newPaginationModel) => {
    setPaginationModel(newPaginationModel);

    setQueryParams((prev) => ({
      ...prev,
      page: newPaginationModel.page + 1,
      limit: newPaginationModel.pageSize,
    }));
  };

  // Clicking the name opens the department read-only, like bank accounts.
  const handleDepartmentView = (department) => {
    setSelectedDepartment(department);
    setIsEditMode(false);
    setIsAddEditDepartmentOpen(true);
  };

  const handleItemClick = (action, department) => {
    setSelectedDepartment(department);
    if (action === "EDIT") {
      setIsEditMode(true);
      setIsAddEditDepartmentOpen(true);
    } else if (action === "DELETE") {
      setIsDeleteConfirmationModalOpen(true);
    }
  };

  const handleDeleteDepartment = async (departmentId) => {
    try {
      await deleteDepartment(departmentId);
      setIsDeleteConfirmationModalOpen(false);
      dispatch(
        showToast({
          message: "Department deleted successfully",
          type: "success",
        }),
      );
      refetchDepartments();
    } catch (error) {
      dispatch(showToast({ message: error?.response?.data?.message, type: "error" }));
    }
  };

  return (
    <>
      <BaseCard elevation={0} className="py-4 px-5 rounded-xl shadow-lg ">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 w-full">
          <div className="w-full sm:w-2/6">
            <BaseTextField
              placeholder="Search by department name"
              value={searchQuery}
              onChange={handleSearchChange}
              startAdornment={<i className="fa-light fa-magnifying-glass text-lg"></i>}
            />
          </div>
          <BaseButton
            color="primary"
            className="!py-[5px] whitespace-nowrap w-full sm:w-auto"
            onClick={() => {
              setSelectedDepartment(null);
              setIsEditMode(true);
              setIsAddEditDepartmentOpen(true);
            }}
          >
            Add New Department
          </BaseButton>
        </div>
      </BaseCard>

      <BaseDataTableV2
        className="max-h-[calc(100vh-320px)] overflow-auto"
        rowHeight={46}
        columns={columns}
        data={departmentsList}
        rowCount={rowCount}
        disableColumnResize={false}
        disableRowSelectionOnClick={true}
        isLoading={isDepartmentsLoading}
        hideFooter={false}
        pagination
        pageSizeOptions={[10, 20, 50]}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationChange}
      />

      {isAddEditDepartmentOpen && (
        <AddEditDepartmentModal
          open={isAddEditDepartmentOpen}
          close={setIsAddEditDepartmentOpen}
          selectedDepartment={selectedDepartment}
          isEditMode={isEditMode}
          setIsEditMode={setIsEditMode}
          setSelectedDepartment={setSelectedDepartment}
          refetchAllDepartments={refetchDepartments}
        />
      )}
      {isDeleteConfirmationModalOpen && (
        <BaseConfirmModal
          open={isDeleteConfirmationModalOpen}
          onClose={() => setIsDeleteConfirmationModalOpen(false)}
          cancelText="Cancel"
          confirmText="Delete"
          confirmButtonColor="error"
          title="Delete Department"
          description={
            <p>
              Are you sure you want to delete the department
              {
                <span className="text-red-600 font-medium mx-1 capitalize">
                  "{selectedDepartment?.name}"
                </span>
              }
              ? This action cannot be undone.
            </p>
          }
          onConfirm={() => handleDeleteDepartment(selectedDepartment?.id)}
        />
      )}
    </>
  );
};

export default CompanyDepartmentsTable;
